import { ArrowRight } from '@ankr.com/ui';
import { Link } from 'react-router';
import {
  Paper,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { JWT } from 'domains/jwtToken/types';
import { ProjectRequestsActivity } from 'domains/projects/screens/ProjectsPage/components/ProjectRequestsActivity';
import { ProjectStatusLabel } from 'domains/projects/components/ProjectStatusLabel';
import { getTimeframeValue } from 'domains/chains/utils/getTimeframeValue';
import { useIsSMDown } from 'uiKit/Theme/useTheme';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import {
  IUseChainProjectItemProps,
  useChainProjectItem,
} from './hooks/useChainProjectItem';
import { ProjectActions } from './components/ProjectActions';
import { chainProjectItemTranslation } from './translation';
import { useChainProjectsSectionStyles } from '../useChainProjectsSectionStyles';

export interface IChainProjectItemProps extends IUseChainProjectItemProps {
  jwts: JWT[];
}

export const ChainProjectItem = ({
  chain,
  jwt,
  jwts,
  timeframe,
}: IChainProjectItemProps) => {
  const { name } = jwt;

  const {
    chain: fullChain,
    handleGoToProject,
    isCurrentChainIncluded,
    isDraft,
    projectPath,
    projectStatus,
    projectStatusLoading,
    projectWhitelistBlockchains,
    projectWhitelistBlockchainsLoading,
    requestsCount,
    requestsCountLoading,
  } = useChainProjectItem({
    chain,
    jwt,
    timeframe,
  });

  const { classes, cx } = useChainProjectsSectionStyles();

  const { keys, t } = useTranslation(chainProjectItemTranslation);

  const shouldUseMobileLayout = useIsSMDown();

  const projectStatusElement = useMemo(() => {
    if (projectStatusLoading) {
      return <Skeleton width={50} />;
    }

    return <ProjectStatusLabel isDraft={isDraft} status={projectStatus} />;
  }, [isDraft, projectStatus, projectStatusLoading]);

  const projectActions = (
    <ProjectActions
      chain={fullChain}
      chainForAddToProject={chain}
      isCurrentChainIncluded={isCurrentChainIncluded}
      jwt={jwt}
      jwts={jwts}
      loading={projectWhitelistBlockchainsLoading}
      projectWhitelistBlockchains={projectWhitelistBlockchains}
    />
  );

  if (shouldUseMobileLayout) {
    return (
      <Paper
        onClick={handleGoToProject}
        className={classes.projectMobileWrapper}
      >
        {projectStatusElement}
        <span className={classes.projectLinkName}>
          {name || t(keys.defaultName)}
        </span>
        <Typography variant="body4" color="textSecondary" mb={1} component="p">
          {t(keys.requestsTitleMobile, {
            timeframe: getTimeframeValue(timeframe),
          })}
        </Typography>
        <ProjectRequestsActivity
          isDisabled={!isCurrentChainIncluded}
          isEmpty={!requestsCount}
          isLoading={requestsCountLoading}
          totalRequestsCount={requestsCount}
        />
        {projectActions}
      </Paper>
    );
  }

  return (
    <TableRow
      onClick={handleGoToProject}
      classes={{ root: classes.projectRow }}
    >
      <TableCell
        className={cx(
          classes.projectsTableCell,
          classes.projectsTableBodyCell,
          classes.projectTableHeadCellNameCell,
        )}
      >
        <Link className={classes.projectLink} to={projectPath}>
          <span className={classes.projectLinkName}>
            {name || t(keys.defaultName)}
          </span>
          {projectStatusElement}
          <ArrowRight className={classes.projectArrowIcon} />
        </Link>
      </TableCell>
      <TableCell
        className={cx(
          classes.projectsTableCell,
          classes.projectsTableBodyCell,
          classes.projectsTableRequestsCell,
        )}
      >
        <ProjectRequestsActivity
          isDisabled={!isCurrentChainIncluded}
          isEmpty={!requestsCount}
          isLoading={requestsCountLoading}
          totalRequestsCount={requestsCount}
        />
      </TableCell>
      <TableCell
        className={cx(
          classes.projectsTableCell,
          classes.projectsTableBodyCell,
          classes.projectsTableButtonsCell,
        )}
        align="right"
      >
        {projectActions}
      </TableCell>
    </TableRow>
  );
};
